import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Bell, Settings, HelpCircle, ChevronDown, 
  LogOut, User, Shield, Sparkles, Menu, CheckCheck, RefreshCw,
  BookOpen, Building2, Calendar, Wrench, AlertCircle, CheckCircle,
  Activity, CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCompany } from '../../context/CompanyContext';

const TopBar = ({ onOpenSkyBot }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showBooksSelector, setShowBooksSelector] = useState(false);
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { companies, selectedCompany, switchCompany } = useCompany();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCompanySwitch = (companyId) => {
    switchCompany(companyId);
    setShowBooksSelector(false);
    // Refresh the page to load new company data
    window.location.reload();
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      manager: 'bg-blue-100 text-blue-800',
      accountant: 'bg-green-100 text-green-800',
      supervisor: 'bg-yellow-100 text-yellow-800',
      clerk: 'bg-gray-100 text-gray-800',
    };
    return colors[role] || colors.clerk;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-emerald-500';
      case 'warning': return 'text-amber-500';
      case 'closed': return 'text-slate-400';
      default: return 'text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-3.5 h-3.5" />;
      case 'warning': return <AlertCircle className="w-3.5 h-3.5" />;
      default: return <Activity className="w-3.5 h-3.5" />;
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-4">
      {/* Left Section */}
      <div className="flex items-center gap-3">
        {/* Books/Company Selector */}
        <div className="relative">
          <button
            onClick={() => setShowBooksSelector(!showBooksSelector)}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="books-selector-btn"
          >
            <Building2 className="w-4 h-4 text-slate-500" />
            <div className="text-left">
              <p className="text-sm font-medium text-slate-700 max-w-[150px] truncate">{selectedCompany?.name || 'Select Company'}</p>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Calendar className="w-3 h-3" />
                <span>FY {selectedCompany?.fiscal_year || '2026'}</span>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>

          {showBooksSelector && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowBooksSelector(false)} />
              <div className="absolute left-0 top-12 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-testid="books-dropdown">
                <div className="p-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase px-2">Switch Company</p>
                </div>
                <div className="max-h-64 overflow-y-auto p-1">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleCompanySwitch(company.id)}
                      className={`w-full flex items-center gap-3 p-2 rounded-md text-left transition-colors
                        ${selectedCompany?.id === company.id ? 'bg-sky-50 border border-sky-200' : 'hover:bg-gray-50'}`}
                      data-testid={`company-option-${company.id}`}
                    >
                      <BookOpen className={`w-5 h-5 ${selectedCompany?.id === company.id ? 'text-sky-600' : 'text-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{company.name}</p>
                        <p className="text-xs text-slate-500">FY {company.fiscal_year}</p>
                      </div>
                      {selectedCompany?.id === company.id && (
                        <CheckCircle className="w-4 h-4 text-sky-600" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="p-2 border-t border-gray-100">
                  <button className="w-full flex items-center justify-center gap-2 text-sm text-sky-600 hover:text-sky-700 py-1.5 hover:bg-sky-50 rounded-md">
                    <RefreshCw className="w-4 h-4" />
                    Repair / Update Books
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Global Search */}
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search vendor, invoice, check #, employee..."
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            data-testid="global-search"
          />
        </div>

        {/* AI Command Box */}
        <button
          onClick={onOpenSkyBot}
          className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-sky-50 border border-purple-200 rounded-lg hover:shadow-md transition-all text-sm"
          data-testid="ai-command-btn"
        >
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="text-slate-600">Ask AI...</span>
        </button>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Quick SkyBot Button */}
        <button
          onClick={onOpenSkyBot}
          className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium"
          data-testid="skybot-topbar-btn"
        >
          <Sparkles className="w-4 h-4" />
          <span>SkyBot</span>
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
              <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1"
                    >
                      <CheckCheck className="w-3 h-3" />
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div 
                        key={notif.id} 
                        onClick={() => markAsRead(notif.id)}
                        className={`p-3 hover:bg-gray-50 border-b border-gray-50 cursor-pointer ${!notif.read ? 'bg-sky-50/50' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-2 h-2 mt-1.5 rounded-full ${
                            notif.type === 'alert' ? 'bg-red-500' : 
                            notif.type === 'success' ? 'bg-green-500' : 
                            notif.type === 'approval' ? 'bg-amber-500' : 'bg-sky-500'
                          }`}></div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{notif.title}</p>
                            <p className="text-xs text-gray-500">{notif.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(notif.time).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t border-gray-100">
                  <button className="w-full text-center text-sm text-sky-600 hover:text-sky-700 py-1">
                    View all notifications
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Help */}
        <button 
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => navigate('/help/center')}
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        {/* Settings */}
        <button 
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => navigate('/setup/company')}
        >
          <Settings className="w-5 h-5" />
        </button>

        {/* User Menu */}
        <div className="relative ml-2">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="user-menu-btn"
          >
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-sky-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {getInitials(user?.name)}
              </div>
            )}
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-gray-700">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role || 'User'}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>

          {showUserMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
              <div className="absolute right-0 top-12 bg-white rounded-lg shadow-xl border border-gray-200 z-50 w-72" data-testid="user-dropdown">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
                    ) : (
                      <div className="w-12 h-12 bg-sky-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {getInitials(user?.name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{user?.name || 'User'}</p>
                      <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${getRoleBadgeColor(user?.role)}`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) || 'User'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                    <User className="w-4 h-4" />
                    My Profile
                  </button>
                  <button 
                    onClick={() => { navigate('/setup/company'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={() => { navigate('/account/subscription'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    data-testid="subscription-menu-link"
                  >
                    <CreditCard className="w-4 h-4" />
                    My Subscription
                  </button>
                  <hr className="my-2" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
